/**
 * Main Promotions page component - Instagram-style mobile promotion display
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Container,
  Paper,
  Fade,
  Slide
} from '@mui/material';
import {
  SignalWifiOff as OfflineIcon
} from '@mui/icons-material';

import { usePromotions } from '../../hooks/usePromotions';
import { promotionTypography } from '../../themes/typography';
import colors from '../../themes/colors';
import PromotionCard from './components/PromotionCard';

/**
 * Hero section with machine info and promotion count
 */
const PromotionHero = ({ storeId, promotionCount, machineName, isOnline, isPreview }) => (
  <Box
    sx={{
      background: colors.promotion.gradient,
      color: 'white',
      py: { xs: 3, md: 4 },
      px: { xs: 2, md: 3 },
      textAlign: 'center',
      position: 'relative'
    }}
  >

    {/* Offline indicator */}
    {!isOnline && (
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          top: 16,
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: 'rgba(255, 255, 255, 0.1)',
          px: 2,
          py: 0.5,
          borderRadius: 2,
          maxWidth: 'calc(100% - 32px)',
          whiteSpace: 'nowrap'
        }}
      >
        <OfflineIcon fontSize="small" />
        <Typography variant="caption">Sin conexión</Typography>
      </Box>
    )}

    {/* Hero content */}
    <Typography
      variant="h4"
      sx={{
        ...promotionTypography.heroTitle,
        color: 'white',
        mb: 1,
        mt: { xs: 4, md: 2 }
      }}
    >
      🎯 {isPreview ? 'Vista Previa - ' : ''}Ofertas Exclusivas
    </Typography>
    
    
    <Typography
      variant="h6"
      sx={{
        ...promotionTypography.discountLarge,
        color: 'white',
        opacity: 0.95
      }}
    >
      ✨ {promotionCount} Promoción{promotionCount !== 1 ? 'es' : ''} Activa{promotionCount !== 1 ? 's' : ''}
    </Typography>
  </Box>
);

/**
 * Empty state when no promotions are available
 */
const EmptyState = ({ isLoading, error }) => (
  <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
    <Typography
      variant="h5"
      sx={{
        ...promotionTypography.heroTitle,
        mb: 2,
        color: 'text.secondary'
      }}
    >
      {error ? '❌ No se pueden cargar las promociones' : '🎪 No hay promociones activas'}
    </Typography>
    
    <Typography
      variant="body1"
      sx={{ color: 'text.secondary', mb: 3 }}
    >
      {error 
        ? 'Por favor verifica tu conexión e inténtalo de nuevo'
        : '¡Vuelve pronto para ver ofertas increíbles!'
      }
    </Typography>

    {error && (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    )}
  </Container>
);

/**
 * Loading skeleton for promotions
 */
const LoadingSkeleton = () => (
  <Box sx={{ px: { xs: 1, md: 2 }, py: 2 }}>
    {[1, 2, 3].map((index) => (
      <Paper
        key={index}
        sx={{
          mb: 3,
          p: 4,
          minHeight: { xs: '400px', md: '450px' },
          borderRadius: { xs: 3, md: 4 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress size={40} />
      </Paper>
    ))}
  </Box>
);

/**
 * Main Promotions component
 */
const Promotions = ({ preview = false }) => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  
  // Get machine name from first promotion (fallback to store ID)
  const [machineName, setMachineName] = useState('');

  // Fetch promotions data
  const {
    promotions,
    loading,
    error,
    isOnline,
    promotionCount,
    isEmpty,
    hasError
  } = usePromotions('sanven', storeId, {
    refreshInterval: 30000, // 30 seconds
    enableAutoRefresh: true,
    enableOfflineCache: true,
    preview: preview
  });

  // Extract machine name from first promotion
  useEffect(() => {
    if (promotions.length > 0 && promotions[0].machineName) {
      setMachineName(promotions[0].machineName);
    }
  }, [promotions]);

  // Handle navigation errors (invalid store ID, etc.)
  useEffect(() => {
    if (!storeId || isNaN(storeId)) {
      navigate('/');
    }
  }, [storeId, navigate]);

  // Override body styles to enable scrolling (fix for global overflow: hidden)
  useEffect(() => {
    // Store original body styles
    const originalStyles = {
      overflow: document.body.style.overflow,
      height: document.body.style.height,
      width: document.body.style.width
    };

    // Apply scrolling-friendly styles
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    document.body.style.width = 'auto';


    // Cleanup: restore original styles when component unmounts
    return () => {
      document.body.style.overflow = originalStyles.overflow;
      document.body.style.height = originalStyles.height;
      document.body.style.width = originalStyles.width;
    };
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default'
      }}
    >
      {/* Hero Section */}
      <PromotionHero
        storeId={storeId}
        promotionCount={promotionCount}
        machineName={machineName}
        isOnline={isOnline}
        isPreview={preview}
      />

      {/* Main Content */}
      <Box>
        {loading && isEmpty ? (
          <LoadingSkeleton />
        ) : hasError || isEmpty ? (
          <EmptyState isLoading={loading} error={error} />
        ) : (
          <Box sx={{ pb: 4 }}>
            {promotions.map((promotion, index) => (
              <Slide
                key={promotion.id}
                direction="up"
                in={true}
                timeout={300 + (index * 100)}
              >
                <Box>
                  <Fade in={true} timeout={500 + (index * 100)}>
                    <Box>
                      <PromotionCard
                        promotion={promotion}
                        index={index}
                      />
                    </Box>
                  </Fade>
                </Box>
              </Slide>
            ))}
          </Box>
        )}
      </Box>

    </Box>
  );
};

export default Promotions;